"use client"

import { useState, useEffect } from "react"
import { Paper, TextField, InputAdornment, Button, Box, CircularProgress, Tooltip } from "@mui/material"
import { DataGrid } from "@mui/x-data-grid"
import { ptBR } from "@mui/x-data-grid/locales"
import useSettingsStyles from "../../settings/common/useSettingsStyles"
import { useTranslation } from "./LocalizationProvider"
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop"
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf"
import * as XLSX from "xlsx"
import jsPDF from "jspdf"
import "jspdf-autotable"
import { useTheme } from "@mui/material/styles"

// Função global para aplicar patch no DOM e remover botões duplicados
const removeDuplicateEditButtons = () => {
  // Função para ser chamada periodicamente
  const checkAndRemoveDuplicates = () => {
    // Procura por menus de contexto abertos
    const menus = document.querySelectorAll(".MuiMenu-paper, .MuiPopover-paper")

    menus.forEach((menu) => {
      // Procura por botões "Editar" dentro do menu
      const editButtons = Array.from(menu.querySelectorAll('button, [role="menuitem"]')).filter((button) => {
        return button.textContent?.includes("Editar")
      })

      // Se encontrar mais de um botão "Editar", remove os extras
      if (editButtons.length > 1) {
        // Mantém o primeiro, remove os outros
        for (let i = 1; i < editButtons.length; i++) {
          editButtons[i].style.display = "none"
        }
      }
    })
  }

  const intervalId = setInterval(checkAndRemoveDuplicates, 100)

  return () => clearInterval(intervalId)
}

const DataTable = ({
  columns,
  rows,
  loading,
  tableName,
  renderBeforeTable,
  renderAfterTable,
  filterable: propFilterable,
  enableDownload: propEnableDownload,
  enablePdfExport: propEnablePdfExport,
  enableXlsxExport: propEnableXlsxExport,
  onPdfExport, // Nova prop
  onXlsxExport, // Nova prop
  pageSizeOptions: propPageSizeOptions,
  getRowId,
  iconColor: propIconColor,
  ...paperProps
}) => {
  const theme = useTheme()
  const defaultIconColor = theme.palette.mode === "dark" ? "inherit" : "action"
  const iconColor = propIconColor ?? defaultIconColor
  const classes = useSettingsStyles()
  const t = useTranslation()
  const [searchKeyword, setSearchKeyword] = useState("")
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 5 })
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    handleResize()

    window.addEventListener("resize", handleResize)

    return () => window.removeEventListener("resize", handleResize)
  }, [])

  useEffect(() => {
    if (isMobile) {
      const cleanup = removeDuplicateEditButtons()
      return cleanup
    }
  }, [isMobile])

  const defaultProps = {
    filterable: true,
    enableDownload: false,
    enablePdfExport: false,
    enableXlsxExport: false,
    pageSize: 5,
    pageSizeOptions: [5, 10, 20, 50, { label: "Todos", value: -1 }],
    xlsxFileName: `${tableName || "export"}.xlsx`,
    pdfFileName: `${tableName || "export"}.pdf`,
  }

  const filterable = typeof propFilterable !== "undefined" ? propFilterable : defaultProps.filterable
  const enableDownload = typeof propEnableDownload !== "undefined" ? propEnableDownload : defaultProps.enableDownload
  const enablePdfExport =
    typeof propEnablePdfExport !== "undefined" ? propEnablePdfExport : enableDownload || defaultProps.enablePdfExport
  const enableXlsxExport =
    typeof propEnableXlsxExport !== "undefined" ? propEnableXlsxExport : enableDownload || defaultProps.enableXlsxExport
  const pageSizeOptions = propPageSizeOptions || defaultProps.pageSizeOptions
  const disableTableNoData = rows.length === 0

  const handleSearchChange = (event) => {
    setSearchKeyword(event.target.value)
  }

  const filteredRows = rows.filter((row) =>
    Object.values(row).some((value) => value?.toString().toLowerCase().includes(searchKeyword.toLowerCase())),
  )

  // Função para obter os dados visíveis na tabela atual
  const getVisibleRows = () => {
    const currentRows = filterable ? filteredRows : rows

    // Se pageSize for -1 (Todos), retorna todas as linhas filtradas
    if (paginationModel.pageSize === -1) {
      return currentRows
    }

    // Calcula o índice inicial e final da página atual
    const startIndex = paginationModel.page * paginationModel.pageSize
    const endIndex = startIndex + paginationModel.pageSize

    return currentRows.slice(startIndex, endIndex)
  }

  const exportToXLSX =
    onXlsxExport ||
    (() => {
      // Usa apenas os dados filtrados da tabela atual
      const currentFilteredRows = filterable ? filteredRows : rows

      // Remove colunas desnecessárias para exportação
      const exportData = currentFilteredRows.map((row) => {
        const cleanRow = { ...row }
        delete cleanRow.photo
        delete cleanRow.actions
        return cleanRow
      })

      const ws = XLSX.utils.json_to_sheet(exportData)
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, tableName || "Dados_Filtrados")
      XLSX.writeFile(wb, defaultProps.xlsxFileName)

      console.log(`Exportados ${exportData.length} registros filtrados`)
    })

  const exportToPDF =
    onPdfExport ||
    (() => {
      // Usa apenas os dados filtrados da tabela atual
      const currentFilteredRows = filterable ? filteredRows : rows
      const doc = new jsPDF()

      doc.text(`${tableName || "Relatório"}`, 14, 15)
      doc.text(`Total de registros filtrados: ${currentFilteredRows.length}`, 14, 25)
      doc.text(`Filtro de busca: ${searchKeyword || "Nenhum"}`, 14, 35)

      const tableColumn = columns
        .filter((col) => col.field !== "actions" && col.field !== "photo")
        .map((col) => col.headerName)

      const tableRows = currentFilteredRows.map((row) =>
        columns
          .filter((col) => col.field !== "actions" && col.field !== "photo")
          .map((col) => {
            const value = row[col.field]
            if (col.renderCell && typeof col.renderCell === "function") {
              try {
                if (col.field === "deviceLastUpdate" && value) {
                  return new Date(value).toLocaleDateString("pt-BR")
                }
                if (col.field === "deviceStatus") {
                  return value || "Desconhecido"
                }
                return value?.toString() || ""
              } catch (e) {
                return value?.toString() || ""
              }
            }
            return value?.toString() || ""
          }),
      )

      doc.autoTable({
        head: [tableColumn],
        body: tableRows,
        startY: 45,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [66, 133, 244], textColor: [255, 255, 255] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      })

      const pageCount = doc.internal.getNumberOfPages()
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i)
        const now = new Date().toLocaleString("pt-BR")
        doc.setFontSize(8)
        doc.text(`Exportado em: ${now}`, 14, doc.internal.pageSize.height - 10)
        doc.text(`Página ${i} de ${pageCount}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10)
      }

      doc.save(defaultProps.pdfFileName)

      console.log(`PDF gerado com ${currentFilteredRows.length} registros filtrados`)
    })

  const handlePaginationChange = (newModel) => {
    setPaginationModel(newModel)
  }

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh", // ou outro valor adequado ao seu layout
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  const shouldShowButtonSection = !disableTableNoData && (enablePdfExport || enableXlsxExport)
  const visibleRowsCount = filterable ? filteredRows.length : rows.length
  const totalFilteredRows = filterable ? filteredRows.length : rows.length

  return (
    <Paper className={classes.table} {...paperProps}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: 2,
          mx: "1%",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        {filterable && (
          <TextField
            label={t("sharedSearch")}
            value={searchKeyword}
            onChange={handleSearchChange}
            variant="outlined"
            InputProps={{
              startAdornment: <InputAdornment position="start">🔍</InputAdornment>,
            }}
            sx={{ flex: 1, minWidth: 200 }}
          />
        )}

        {shouldShowButtonSection && (
          <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
            {enableXlsxExport && (
              <Tooltip title={`Exportar ${visibleRowsCount} registros visíveis para Excel`}>
                <Button variant="contained" onClick={exportToXLSX}>
                  <LocalPrintshopIcon color={iconColor} />
                  <Box component="span" sx={{ pl: 0.5 }}>
                    {t("reportExport")}
                  </Box>
                </Button>
              </Tooltip>
            )}

            {enablePdfExport && (
              <Tooltip title={`Exportar ${visibleRowsCount} registros visíveis para PDF`}>
                <Button variant="contained" onClick={exportToPDF} sx={{ zIndex: 1 }}>
                  <PictureAsPdfIcon color={iconColor} />
                  <Box component="span" sx={{ pl: 0.5, color: { iconColor } }}>
                    PDF
                  </Box>
                </Button>
              </Tooltip>
            )}
          </Box>
        )}
      </Box>

      {renderBeforeTable}

      <DataGrid
        rows={filterable ? filteredRows : rows}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={handlePaginationChange}
        pageSizeOptions={pageSizeOptions}
        getRowId={getRowId}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          border: 0,
          height: 500, // Altura fixa para permitir scroll
          "& .MuiDataGrid-main": {
            overflow: "auto", // Permite scroll no conteúdo principal
          },
          "& .MuiDataGrid-virtualScroller": {
            overflow: "auto", // Scroll vertical para as linhas
            maxHeight: "400px", // Altura máxima da área de scroll
          },
          "& .MuiDataGrid-cell": {
            display: "flex",
            alignItems: "center",
            wordWrap: "break-word",
            height: "auto",
            lineHeight: "1.4",
            overflowWrap: "break-word",
            p: 1,
            minHeight: "52px", // Altura mínima das células
          },
          "& .MuiDataGrid-row": {
            height: "auto",
            minHeight: "52px", // Altura mínima das linhas
            "&:hover": {
              backgroundColor: "rgba(0, 0, 0, 0.04)", // Efeito hover
            },
          },
          "& .MuiDataGrid-footerContainer": {
            borderTop: "1px solid",
            borderColor: "divider",
            position: "sticky", // Footer fixo na parte inferior
            bottom: 0,
            backgroundColor: "background.paper",
            zIndex: 1,
          },
          // Estilo do cabeçalho (header) adaptável ao tema
          "& .MuiDataGrid-columnHeaders": {
            background: (theme) =>
              theme.palette.mode === "dark"
                ? "linear-gradient(135deg, #1E3A8A 0%, #3B82F6 100%)" // Gradiente azul (dark)
                : "linear-gradient(135deg, #3B82F6 0%, #93C5FD 100%)", // Gradiente azul claro (light)
            color: (theme) => (theme.palette.mode === "dark" ? theme.palette.common.white : theme.palette.common.black),
            fontWeight: "bold",
            fontSize: "0.875rem",
            borderBottom: "none",
            boxShadow: (theme) =>
              theme.palette.mode === "dark" ? "0 2px 8px rgba(0, 0, 0, 0.5)" : "0 2px 8px rgba(0, 0, 0, 0.1)",
            position: "sticky", // Header fixo no topo
            top: 0,
            zIndex: 2,
          },
          "& .MuiDataGrid-columnHeaderTitle": {
            fontWeight: "bold !important",
          },
          // Estilo da barra de scroll
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar": {
            width: "8px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-track": {
            backgroundColor: "rgba(0,0,0,0.1)",
            borderRadius: "4px",
          },
          "& .MuiDataGrid-virtualScroller::-webkit-scrollbar-thumb": {
            backgroundColor: "rgba(0,0,0,0.3)",
            borderRadius: "4px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.5)",
            },
          },
        }}
      />

      {renderAfterTable}
    </Paper>
  )
}

export default DataTable